import type { TFunction } from 'i18next';
import { isAxiosError } from 'axios';
import { useCallback } from 'react';
import useU2000AxiosAuthenticated from '../../../../../u2000Core/axios/U2000UseAxiosAuthenticated';
import { U2000HandleApiError } from '../../../../../u2000Core/axios/U2000HandleApiError';
import {
  doResolveLotDetailForGraphAdd,
  luHandleAppiError,
  U3141_ERROR_CHILD_LOT_ALREADY_IN_CHAIN,
  type U3141ResolveLotDetailForGraphAddBody,
  type U3141ResolveLotDetailForGraphAddResult,
} from '../../../../U3141Service';
import { NS } from '../../../../U3141';
import {
  u3141ParseResolveLotDetailForGraphAddResponse,
  u3141ResolveGraphAddNotFoundResult,
} from '../../../../tools/u3141GraphAddResolveMapper';

function shouldToastResolveFailure(status: number | undefined): boolean {
  if (status == null) return false;
  return status === 401 || status === 403;
}

export function useU3141ResolveLotDetailForGraphAddHandlers(params: {
  t: TFunction;
  toast: { error: (text: string, title?: string) => void };
}) {
  const { t, toast } = params;
  const axios = useU2000AxiosAuthenticated();

  const resolveLotForGraphAdd = useCallback(
    async (
      masterLotChainId: string,
      body: U3141ResolveLotDetailForGraphAddBody,
    ): Promise<U3141ResolveLotDetailForGraphAddResult | null> => {
      const trimmed = body.lot.trim();
      if (!trimmed) {
        toast.error(t('errAddLotFromGraphLotRequired', { ns: NS }), t('errorTitle', { ns: NS }));
        return null;
      }
      try {
        const res = await doResolveLotDetailForGraphAdd(axios, masterLotChainId, {
          ...body,
          lot: trimmed,
        });
        const result = u3141ParseResolveLotDetailForGraphAddResponse(res.data);
        if (!result) {
          return u3141ResolveGraphAddNotFoundResult();
        }
        if (result.alreadyInMasterChain) {
          toast.error(t('errChildLotAlreadyInChain', { ns: NS }), t('errorTitle', { ns: NS }));
          return result;
        }
        return result;
      } catch (error) {
        const status = isAxiosError(error) ? error.response?.status : undefined;
        const apiErrors = isAxiosError(error)
          ? (error.response?.data as { errors?: { errorCode?: string }[] } | undefined)?.errors
          : undefined;
        if (
          Array.isArray(apiErrors) &&
          apiErrors.some((e) => e?.errorCode === U3141_ERROR_CHILD_LOT_ALREADY_IN_CHAIN)
        ) {
          toast.error(t('errChildLotAlreadyInChain', { ns: NS }), t('errorTitle', { ns: NS }));
          return { found: true, alreadyInMasterChain: true };
        }
        if (shouldToastResolveFailure(status)) {
          const errorResult = U2000HandleApiError(
            error as Parameters<typeof U2000HandleApiError>[0],
            t,
            NS,
            luHandleAppiError,
          );
          if (errorResult.genericErrors?.length) {
            errorResult.genericErrors.forEach((errItem) => toast.error(errItem.text, errItem.title));
          } else {
            toast.error(t('errorTitle', { ns: NS }), t('errorTitle', { ns: NS }));
          }
          return null;
        }
        return u3141ResolveGraphAddNotFoundResult();
      }
    },
    [axios, t, toast],
  );

  return { resolveLotForGraphAdd };
}
